/**
 * LoginScreen — OAuth (Google / Microsoft / GitHub) + magic-link email.
 * F0 shell: wires the auth API, but the backend endpoints are not live yet,
 * so failures surface inline without crashing.
 */

import { useState, type FormEvent, type ReactElement } from 'react';
import { authApi, type OAuthProvider } from '@/features/auth/authApi';

const OAUTH_PROVIDERS: { id: OAuthProvider; label: string }[] = [
  { id: 'google', label: 'Continuar com Google' },
  { id: 'microsoft', label: 'Continuar com Microsoft' },
  { id: 'github', label: 'Continuar com GitHub' },
];

export function LoginScreen(): ReactElement {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() => {
    const code = new URLSearchParams(window.location.search).get('error');
    switch (code) {
      case 'magic_link_invalid':
        return 'O link mágico é inválido, expirou ou já foi usado. Peça um novo.';
      case 'session_failed':
        return 'Não foi possível concluir o login. Tente novamente.';
      case 'oauth_failed':
      case 'no_oauth_flow':
        return 'Falha no login social. Tente novamente.';
      default:
        return null;
    }
  });

  async function handleOAuth(provider: OAuthProvider): Promise<void> {
    setError(null);
    setMessage(null);
    setPending(provider);
    try {
      const { redirect_url } = await authApi.startOAuth(provider);
      window.location.assign(redirect_url);
    } catch {
      setError(`Não foi possível iniciar login com ${provider}.`);
      setPending(null);
    }
  }

  async function handleMagicLink(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setPending('magic-link');
    try {
      await authApi.requestMagicLink(email);
      setMessage(`Link de acesso enviado para ${email}.`);
    } catch {
      setError('Não foi possível enviar o link. Verifique o e-mail.');
    } finally {
      setPending(null);
    }
  }

  return (
    <main style={styles.page}>
      <section style={styles.card}>
        <h1 style={styles.title}>RealtimeTranslator</h1>
        <p style={styles.subtitle}>Entre para criar e controlar sessões.</p>

        <div style={styles.oauthGroup}>
          {OAUTH_PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              style={styles.oauthButton}
              disabled={pending !== null}
              onClick={() => void handleOAuth(p.id)}
            >
              {pending === p.id ? 'Redirecionando…' : p.label}
            </button>
          ))}
        </div>

        <div style={styles.divider}>
          <span style={styles.dividerText}>ou por e-mail</span>
        </div>

        <form onSubmit={(e) => void handleMagicLink(e)} style={styles.form}>
          <label htmlFor="email" style={styles.label}>
            E-mail
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            placeholder="voce@empresa.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
          />
          <button
            type="submit"
            style={styles.primaryButton}
            disabled={pending !== null || email.length === 0}
          >
            {pending === 'magic-link' ? 'Enviando…' : 'Enviar link mágico'}
          </button>
        </form>

        {message && <p style={styles.success}>{message}</p>}
        {error && <p style={styles.error}>{error}</p>}
      </section>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    padding: '1.5rem',
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: 'var(--rt-surface)',
    border: '1px solid var(--rt-border)',
    borderRadius: 12,
    padding: '2rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  title: { margin: 0, fontSize: '1.5rem' },
  subtitle: { margin: 0, color: 'var(--rt-text-muted)', fontSize: '0.9rem' },
  oauthGroup: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  oauthButton: {
    padding: '0.7rem 1rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'transparent',
    color: 'var(--rt-text)',
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
  divider: { textAlign: 'center', borderTop: '1px solid var(--rt-border)' },
  dividerText: {
    position: 'relative',
    top: '-0.7rem',
    background: 'var(--rt-surface)',
    padding: '0 0.5rem',
    color: 'var(--rt-text-muted)',
    fontSize: '0.8rem',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  label: { fontSize: '0.85rem', color: 'var(--rt-text-muted)' },
  input: {
    padding: '0.7rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    fontSize: '0.95rem',
  },
  primaryButton: {
    padding: '0.7rem 1rem',
    borderRadius: 8,
    border: 'none',
    background: 'var(--rt-primary)',
    color: 'var(--rt-primary-text)',
    cursor: 'pointer',
    fontSize: '0.95rem',
    marginTop: '0.25rem',
  },
  success: { color: 'var(--rt-success)', fontSize: '0.85rem', margin: 0 },
  error: { color: 'var(--rt-danger)', fontSize: '0.85rem', margin: 0 },
};
